/**
 * "Organization" tab of the runtime System administration console — where a
 * customer organization's functional administrator adds their OWN colleagues
 * to the application their organization was granted.
 *
 * The endpoints (`/team/my-tenant/members`, `/team/my-tenant/invite`) already
 * existed and were reachable only by hand: a func-admin holds RUNTIME_USER, so
 * they cannot open the workbench where the project's Team tab lives, and no
 * screen anywhere called them. The practical effect was that every new
 * colleague had to be added by someone else through an API call, and until
 * then they signed in successfully and met "No applications available".
 *
 * Deliberately just two things — see who is in, and let someone in:
 *   - a colleague who ALREADY has an account in this organization (every SSO
 *     user does, from their first sign-in) is added immediately, no email;
 *   - anyone else is emailed an invitation that lands them in this
 *     organization.
 * Removing a member and changing their roles stay with the project owner: this
 * console administers an application it does not own, and revoking someone
 * else's grant is not the same decision as extending your own.
 */
import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { Loader2, Mail, UserPlus, Users } from 'lucide-react'
import {
  inviteToMyTenant, listMyTenantMembers,
  type ProjectMemberInfo, type TenantInfo,
} from '@/api/team'

// Runtime-only roles: the two a customer organization may hand out.
const ROLES: { value: string; label: string; hint: string }[] = [
  {
    value: 'RUNTIME_USER',
    label: 'Runtime User',
    hint: 'Use the application. Cannot open the designers.',
  },
  {
    value: 'APPLICATION_USER',
    label: 'Application User',
    hint: 'Use the application, and read its screens in preview.',
  },
]

const inp: React.CSSProperties = {
  padding: '8px 10px', border: '1px solid #cbd5e1', borderRadius: 6,
  fontSize: 14, background: '#fff', color: '#1f2937', outline: 'none', width: '100%',
}
const btn = (primary?: boolean): React.CSSProperties => ({
  padding: '7px 14px', borderRadius: 8, fontSize: 14, fontWeight: 600,
  cursor: 'pointer', border: primary ? 'none' : '1px solid #cbd5e1',
  background: primary ? '#4f46e5' : '#fff', color: primary ? '#fff' : '#334155',
  display: 'inline-flex', alignItems: 'center', gap: 6,
})

function errText(e: unknown): string {
  const d = (e as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
  return typeof d === 'string' && d.trim() ? d.trim() : ''
}

export function OrganizationUsersPanel({ projectId }: { projectId: string }) {
  const [tenant, setTenant] = useState<TenantInfo | null>(null)
  const [members, setMembers] = useState<ProjectMemberInfo[] | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const [email, setEmail] = useState('')
  const [role, setRole] = useState(ROLES[0].value)
  const [busy, setBusy] = useState(false)

  const load = useCallback(() => {
    setErr(null)
    listMyTenantMembers(projectId)
      .then((r) => { setTenant(r.tenant); setMembers(r.members || []) })
      .catch((e) => {
        setMembers([])
        setErr(errText(e) || 'Could not load your organization\'s users')
      })
  }, [projectId])

  useEffect(() => { load() }, [load])

  const roleHint = useMemo(
    () => ROLES.find((r) => r.value === role)?.hint || '', [role],
  )

  const invite = async () => {
    const addr = email.trim()
    if (!addr) return
    setBusy(true)
    try {
      const res = await inviteToMyTenant(projectId, addr, [role])
      // The two outcomes need different words: one is done, the other is a
      // letter in the post. Saying "invited" for both leaves an admin waiting
      // for an acceptance that already happened.
      if (res.mode === 'added_existing') {
        toast.success(`${addr} can now use this application`)
      } else if (res.email_sent) {
        toast.success(`Invitation emailed to ${addr}`)
      } else {
        toast(`${addr} was invited, but the email could not be sent — `
          + 'send them the invitation link from the project owner.')
      }
      setEmail('')
      load()
    } catch (e) {
      toast.error(errText(e) || 'Could not add that person')
    } finally { setBusy(false) }
  }

  if (members === null) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#64748b' }}>
        <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> Loading…
      </div>
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, color: '#1f2937' }}>
      <p style={{ margin: 0, fontSize: 14, color: '#64748b' }}>
        The people in <b>{tenant?.tenant_name || 'your organization'}</b> who can use this
        application. Everyone here works in your organization's own data — never the
        application owner's.
      </p>

      {err && (
        <div style={{
          fontSize: 13.5, background: '#fef2f2', color: '#991b1b',
          border: '1px solid #fecaca', borderRadius: 8, padding: '9px 11px',
        }}>{err}</div>
      )}

      <div style={{
        border: '1px solid #e2e8f0', borderRadius: 10, padding: 12,
        display: 'flex', flexDirection: 'column', gap: 10,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 14, fontWeight: 600 }}>
          <UserPlus size={15} /> Add someone from your organization
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <input
            style={{ ...inp, flex: '1 1 240px', width: 'auto' }}
            type="email" placeholder="name@yourcompany.com" value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !busy) invite() }}
          />
          <select
            style={{ ...inp, width: 'auto' }} value={role}
            onChange={(e) => setRole(e.target.value)}
          >
            {ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select>
          <button type="button" style={btn(true)} disabled={busy || !email.trim()} onClick={invite}>
            {busy ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Mail size={14} />}
            Add
          </button>
        </div>
        <span style={{ fontSize: 13, color: '#64748b' }}>
          {roleHint} Someone who already has an account here starts using the
          application straight away; anyone else is emailed an invitation.
        </span>
      </div>

      <div>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 7, fontSize: 14,
          fontWeight: 600, marginBottom: 8,
        }}>
          <Users size={15} /> Users ({members.length})
        </div>
        {members.length === 0 ? (
          <div style={{ fontSize: 13.5, color: '#64748b' }}>
            Nobody from your organization has been added yet.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {members.map((m) => (
              <div
                key={m.id}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  gap: 10, border: '1px solid #e2e8f0', borderRadius: 8,
                  padding: '8px 11px', fontSize: 14,
                  background: m.is_active === false ? '#f8fafc' : '#fff',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {m.user_name || m.email}
                    {m.is_active === false && (
                      <span style={{ fontWeight: 400, color: '#94a3b8' }}> · deactivated</span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: '#64748b' }}>{m.email}</div>
                </div>
                <div style={{ fontSize: 13, color: '#475569', whiteSpace: 'nowrap' }}>
                  {(m.role_display?.length ? m.role_display : m.roles).join(', ')}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default OrganizationUsersPanel
